import type { PPAScenarioColumnVisibilityInput, PPAGetScenarioViewResponse, PPAGetViewMetadataResponse } from 'types/ppaViews';
import { hasCatalogLensSelection } from './scenarioLensCatalog.helpers';
import type { LensCatalogData } from './scenarioLensCatalog.types';
import type { LiveViewConfig, ScenarioViewData, ScenarioViewLens } from './scenarioLens.types';
import {
    areLensMappingsEqual,
    computeSelectionDirtyLensIds,
    flattenCatalogToColumnVisibility,
} from './viewSavePayload.helpers';

export const mapPpaViewMetadataToScenarioViewData = (
    metadata: PPAGetViewMetadataResponse,
): ScenarioViewData => {
    const activeLensId =
        metadata.active_lens_id ??
        metadata.lenses.find((lens) => lens.is_default_lens)?.lens_id ??
        metadata.lenses.find((lens) => lens.is_selected)?.lens_id ??
        metadata.lenses[0]?.lens_id ??
        null;

    return {
        view_id: metadata.view_id,
        view_name: metadata.view_name,
        view_type: metadata.view_type,
        created_by: metadata.created_by,
        is_default: metadata.is_default,
        updated_at: metadata.updated_at,
        scope: metadata.filters,
        group_by: metadata.group_by.map((item) => ({
            attribute_id: 0,
            attribute_key: item.attribute_key,
            display_name: item.display_name,
            display_order: item.display_order,
        })),
        lenses: metadata.lenses.map((lens) => ({
            lens_id: lens.lens_id,
            lens_name: lens.lens_name,
            is_private: Boolean(lens.is_private),
            is_selected: lens.is_selected,
            display_order: lens.display_order,
            is_active_lens: activeLensId != null && lens.lens_id === activeLensId,
            is_default_lens: lens.is_default_lens,
        })),
        column_visibility: metadata.column_visibility?.map((item) => ({
            lens_id: item.lens_id,
            attribute_key: item.attribute_key,
            item_type: item.item_type,
            is_display_enabled: item.is_display_enabled,
        })),
        pending_messages: metadata.pending_messages ?? [],
    };
};

export const mapPpaScenarioViewToScenarioViewData = (
    scenarioView: PPAGetScenarioViewResponse,
): ScenarioViewData => {
    const activeLensId =
        scenarioView.active_lens_id ??
        scenarioView.lenses.find((lens) => lens.is_selected)?.lens_id ??
        scenarioView.lenses[0]?.lens_id ??
        null;

    return {
        view_id: scenarioView.base_view_id,
        view_name: scenarioView.has_deviation ? 'Scenario View' : scenarioView.base_view_name,
        view_type: scenarioView.has_deviation ? 'SCENARIO' : 'SYSTEM',
        created_by: null,
        is_default: false,
        scope: scenarioView.filters,
        group_by: scenarioView.group_by.map((item) => ({
            attribute_id: 0,
            attribute_key: item.attribute_key,
            display_name: item.display_name,
            display_order: item.display_order,
        })),
        lenses: scenarioView.lenses.map((lens) => ({
            lens_id: lens.lens_id,
            lens_name: lens.lens_name,
            is_private: lens.lens_type === 'CUSTOM',
            is_selected: lens.is_selected,
            display_order: lens.display_order,
            is_active_lens: activeLensId != null && lens.lens_id === activeLensId,
        })),
        column_visibility: scenarioView.column_visibility?.map((item) => ({
            lens_id: item.lens_id,
            attribute_key: item.attribute_key,
            item_type: item.item_type,
            is_display_enabled: item.is_display_enabled,
        })),
    };
};

/** Build metadata cache seed from live workspace config (matches create payload source). */
export const buildScenarioViewSeedFromLiveConfig = (
    base: ScenarioViewData,
    live: LiveViewConfig,
    created: {
        viewId: number;
        viewName: string;
        isDefault: boolean;
        filters?: unknown[];
    },
): ScenarioViewData => {
    const lensConfigById = new Map(live.lens_config.map((item) => [item.lens_id, item]));
    const groupByOrderByKey = new Map(
        live.group_by.map((item) => [item.attribute_key, item.display_order]),
    );

    return {
        ...base,
        view_id: created.viewId,
        view_name: created.viewName,
        is_default: created.isDefault,
        updated_at: null,
        scope: created.filters ?? base.scope,
        pending_messages: [],
        column_visibility: live.column_visibility,
        group_by: base.group_by
            .filter((item) => groupByOrderByKey.has(item.attribute_key))
            .map((item) => ({
                ...item,
                display_order: groupByOrderByKey.get(item.attribute_key) ?? item.display_order,
            }))
            .sort((left, right) => left.display_order - right.display_order),
        lenses: base.lenses.map((lens) => {
            const config = lensConfigById.get(lens.lens_id);
            if (!config) {
                return lens;
            }

            return {
                ...lens,
                is_selected: config.is_selected,
                display_order: config.display_order,
                is_active_lens: live.active_lens_id === lens.lens_id,
                is_default_lens: config.is_default_lens,
            };
        }),
    };
};

export const cloneScenarioView = (view: ScenarioViewData): ScenarioViewData =>
    JSON.parse(JSON.stringify(view)) as ScenarioViewData;

const normalizeScenarioViewLens = (lens: ScenarioViewLens & { is_custom?: boolean }): ScenarioViewLens => ({
    ...lens,
    is_private: Boolean(lens.is_private ?? lens.is_custom),
});

export const normalizeScenarioView = (view: ScenarioViewData): ScenarioViewData => ({
    ...view,
    lenses: view.lenses.map((lens) => normalizeScenarioViewLens(lens)),
});

/** Scenario deviation with no selected lenses cannot render lens tabs or table columns. */
export const isPpaScenarioViewMetadataUnusable = (metadata: ScenarioViewData): boolean => {
    const hasSelectedLens = metadata.lenses.some((lens) => lens.is_selected);
    if (!hasSelectedLens) {
        return true;
    }

    const hasEnabledColumns = (metadata.column_visibility ?? []).some((column) => column.is_display_enabled);
    return !hasEnabledColumns;
};

export const sortLensesByDisplayOrder = (lenses: ScenarioViewLens[]) =>
    [...lenses].sort((left, right) => left.display_order - right.display_order);

/** Keeps settings draft in sync when view gains lenses locally (e.g. new custom lens). */
export const mergeViewLensesIntoDraft = (
    view: ScenarioViewData,
    draftLenses: ScenarioViewLens[],
): ScenarioViewLens[] => {
    const viewLensById = new Map(view.lenses.map((lens) => [lens.lens_id, lens]));
    const draftIds = new Set(draftLenses.map((lens) => lens.lens_id));
    const missingFromDraft = view.lenses.filter((lens) => !draftIds.has(lens.lens_id));

    const syncedDraft = draftLenses.map((draftLens) => {
        const viewLens = viewLensById.get(draftLens.lens_id);
        if (!viewLens) {
            return draftLens;
        }

        return {
            ...draftLens,
            lens_name: viewLens.lens_name,
            is_private: viewLens.is_private,
        };
    });

    if (missingFromDraft.length === 0) {
        return sortLensesByDisplayOrder(syncedDraft);
    }

    return sortLensesByDisplayOrder([...syncedDraft, ...missingFromDraft]);
};

export const getVisibleLenses = (view: ScenarioViewData) =>
    sortLensesByDisplayOrder(view.lenses.filter((lens) => lens.is_selected));

export const getActiveLens = (view: ScenarioViewData) => {
    const visibleLenses = getVisibleLenses(view);
    return visibleLenses.find((lens) => lens.is_active_lens) ?? visibleLenses[0] ?? null;
};

const pickFallbackActiveLensId = (visibleLenses: ScenarioViewLens[]): number | null => {
    if (!visibleLenses.length) {
        return null;
    }

    const defaultLens = visibleLenses.find((lens) => lens.is_default_lens);
    if (defaultLens) {
        return defaultLens.lens_id;
    }

    const firstSystemLens = sortLensesByDisplayOrder(
        visibleLenses.filter((lens) => !lens.is_private),
    )[0];
    if (firstSystemLens) {
        return firstSystemLens.lens_id;
    }

    return visibleLenses[0].lens_id;
};

const setActiveLens = (view: ScenarioViewData, lensId: number): ScenarioViewData => ({
    ...view,
    lenses: view.lenses.map((lens) => ({
        ...lens,
        is_active_lens: lens.lens_id === lensId,
    })),
});

export const normalizeLensDisplayOrder = (lenses: ScenarioViewLens[]) =>
    sortLensesByDisplayOrder(lenses).map((lens, index) => ({
        ...lens,
        display_order: index + 1,
    }));

export const toggleLensSelection = (lenses: ScenarioViewLens[], lensId: number, isSelected: boolean) =>
    lenses.map((lens) => (lens.lens_id === lensId ? { ...lens, is_selected: isSelected } : lens));

/** Selected lenses first, then unselected — preserves relative order within each group. */
export const reorderLensesSelectedFirst = (lenses: ScenarioViewLens[]): ScenarioViewLens[] => {
    const orderedLenses = sortLensesByDisplayOrder(lenses);
    const selectedLenses = orderedLenses.filter((lens) => lens.is_selected);
    const unselectedLenses = orderedLenses.filter((lens) => !lens.is_selected);

    return [...selectedLenses, ...unselectedLenses].map((lens, index) => ({
        ...lens,
        display_order: index + 1,
    }));
};

/** When selecting via catalog, append to the end of the draggable group; unselected lenses sink below. */
export const reorderLensOnCatalogSelectionChange = (
    lenses: ScenarioViewLens[],
    lensId: number,
    isSelected: boolean,
): ScenarioViewLens[] => {
    const updatedLenses = toggleLensSelection(lenses, lensId, isSelected);
    const orderedLenses = sortLensesByDisplayOrder(updatedLenses);

    if (!isSelected) {
        return reorderLensesSelectedFirst(updatedLenses);
    }

    const selectedLenses = orderedLenses.filter((lens) => lens.is_selected && lens.lens_id !== lensId);
    const unselectedLenses = orderedLenses.filter((lens) => !lens.is_selected);
    const targetLens = orderedLenses.find((lens) => lens.lens_id === lensId);
    if (!targetLens) {
        return reorderLensesSelectedFirst(updatedLenses);
    }

    return [...selectedLenses, targetLens, ...unselectedLenses].map((lens, index) => ({
        ...lens,
        display_order: index + 1,
    }));
};

const getNextCustomLensId = (lenses: ScenarioViewLens[]) =>
    lenses.reduce((maxId, lens) => Math.max(maxId, lens.lens_id), 0) + 1;

export const appendCustomLens = (
    view: ScenarioViewData,
    lensName: string,
    lensId?: number,
    options?: { selectLens?: boolean },
): ScenarioViewData => {
    const selectLens = options?.selectLens ?? false;
    const nextLensId = lensId ?? getNextCustomLensId(view.lenses);
    const nextDisplayOrder = view.lenses.length + 1;
    const nextLens: ScenarioViewLens = {
        lens_id: nextLensId,
        lens_name: lensName,
        is_private: true,
        is_selected: selectLens,
        display_order: nextDisplayOrder,
        is_active_lens: false,
    };

    const nextLenses = normalizeLensDisplayOrder([...view.lenses, nextLens]);

    if (!selectLens) {
        return ensureActiveLensIsVisible({
            ...view,
            lenses: nextLenses,
        });
    }

    return setActiveLens(
        {
            ...view,
            lenses: nextLenses.map((lens) => ({
                ...lens,
                is_active_lens: lens.lens_id === nextLensId,
            })),
        },
        nextLensId,
    );
};

export const removeCustomLensFromView = (view: ScenarioViewData, lensId: number): ScenarioViewData =>
    ensureActiveLensIsVisible({
        ...view,
        lenses: normalizeLensDisplayOrder(view.lenses.filter((lens) => lens.lens_id !== lensId)),
    });

export const renameCustomLensInView = (
    view: ScenarioViewData,
    lensId: number,
    lensName: string,
): ScenarioViewData => ({
    ...view,
    lenses: view.lenses.map((lens) =>
        lens.lens_id === lensId ? { ...lens, lens_name: lensName } : lens,
    ),
});

const ensureActiveLensIsVisible = (view: ScenarioViewData): ScenarioViewData => {
    const visibleLenses = getVisibleLenses(view);
    if (!visibleLenses.length) {
        return view;
    }

    const activeLens = getActiveLens(view);
    if (activeLens && visibleLenses.some((lens) => lens.lens_id === activeLens.lens_id)) {
        return view;
    }

    return setActiveLens(view, pickFallbackActiveLensId(visibleLenses) ?? visibleLenses[0].lens_id);
};

export const applyLensDraftToView = (view: ScenarioViewData, draftLenses: ScenarioViewLens[]): ScenarioViewData =>
    ensureActiveLensIsVisible({
        ...view,
        lenses: normalizeLensDisplayOrder(draftLenses),
    });

const serializeLensSettingsState = (lenses: ScenarioViewLens[]) =>
    sortLensesByDisplayOrder(lenses)
        .map((lens) => `${lens.lens_id}:${lens.is_selected ? 1 : 0}:${lens.display_order}`)
        .join('|');

export const getActiveLensIdFromLenses = (lenses: ScenarioViewLens[]): number | null =>
    lenses.find((lens) => lens.is_active_lens)?.lens_id ?? null;

export const hasActiveLensChanged = (
    baselineLenses: ScenarioViewLens[],
    nextLenses: ScenarioViewLens[],
): boolean =>
    getActiveLensIdFromLenses(baselineLenses) !== getActiveLensIdFromLenses(nextLenses);

export const VIEW_DIRTY_REASON_LABELS = {
    lens_mappings: 'Lens tab selection or display order changed',
    catalog_column_visibility: 'Column visibility edited (catalog draft)',
    settings_panel: 'Lens settings panel draft changed',
    custom_lens_added: 'Custom lens added locally',
} as const;

export type ViewDirtyReason = keyof typeof VIEW_DIRTY_REASON_LABELS;

export type ViewDirtyDiagnostics = {
    isDirty: boolean;
    reasons: ViewDirtyReason[];
    details: {
        baselineActiveLensId: number | null;
        currentActiveLensId: number | null;
        dirtyCatalogLensIds: number[];
        lensMappingsChanged: boolean;
        settingsPanelChanged: boolean;
    };
};

export const diagnoseViewDirtyState = (params: {
    baselineLenses: ScenarioViewLens[];
    viewLenses: ScenarioViewLens[];
    draftLenses: ScenarioViewLens[];
    catalogDraftsByLensId: Record<number, LensCatalogData>;
    resolveBaselineCatalog: (lensId: number) => LensCatalogData | null;
}): ViewDirtyDiagnostics => {
    const baselineActiveLensId = getActiveLensIdFromLenses(params.baselineLenses);
    const currentActiveLensId = getActiveLensIdFromLenses(params.viewLenses);
    const lensMappingsChanged = !areLensMappingsEqual(params.baselineLenses, params.viewLenses);
    const dirtyCatalogLensIds = computeSelectionDirtyLensIds(
        params.catalogDraftsByLensId,
        params.resolveBaselineCatalog,
    );
    const settingsPanelChanged = hasSettingsPanelDraftChanges(
        params.viewLenses,
        params.draftLenses,
        params.catalogDraftsByLensId,
    );

    const reasons: ViewDirtyReason[] = [];
    if (lensMappingsChanged) {
        reasons.push('lens_mappings');
    }
    if (dirtyCatalogLensIds.length > 0) {
        reasons.push('catalog_column_visibility');
    }
    if (settingsPanelChanged) {
        reasons.push('settings_panel');
    }

    return {
        isDirty: reasons.length > 0,
        reasons,
        details: {
            baselineActiveLensId,
            currentActiveLensId,
            dirtyCatalogLensIds,
            lensMappingsChanged,
            settingsPanelChanged,
        },
    };
};

/** True when local view state differs from the baseline captured at load/last save. */
export const computeViewHasLocalEdits = (params: {
    baselineLenses: ScenarioViewLens[];
    viewLenses: ScenarioViewLens[];
    draftLenses: ScenarioViewLens[];
    catalogDraftsByLensId: Record<number, LensCatalogData>;
    resolveBaselineCatalog: (lensId: number) => LensCatalogData | null;
}): boolean => diagnoseViewDirtyState(params).isDirty;

/** True when lens settings panel drafts differ from the persisted view (order, selection, or catalog). */
export const hasSettingsPanelDraftChanges = (
    viewLenses: ScenarioViewLens[],
    draftLenses: ScenarioViewLens[],
    catalogDraftsByLensId: Record<number, LensCatalogData>,
): boolean => {
    if (Object.keys(catalogDraftsByLensId).length > 0) {
        return true;
    }

    return serializeLensSettingsState(viewLenses) !== serializeLensSettingsState(draftLenses);
};

export const countPrivateLenses = (lenses: ScenarioViewLens[]) =>
    lenses.filter((lens) => lens.is_private).length;

export function flattenCatalogDraftsToColumnVisibility(
    catalogDraftsByLensId: Record<number, LensCatalogData>,
): PPAScenarioColumnVisibilityInput[] {
    return Object.entries(catalogDraftsByLensId).flatMap(([lensId, draft]) =>
        flattenCatalogToColumnVisibility(Number(lensId), draft),
    );
}

export function deriveLensIsSelected(catalog: LensCatalogData): boolean {
    return hasCatalogLensSelection(catalog);
}

/** Updates lens tab visibility from catalog drafts / resolved catalogs (>=1 selected attribute). */
export const syncViewLensSelectionFromCatalogs = (
    view: ScenarioViewData,
    resolveLensIsSelectedForLens: (lensId: number) => boolean | null,
): ScenarioViewData =>
    ensureActiveLensIsVisible({
        ...view,
        lenses: reorderLensesSelectedFirst(
            view.lenses.map((lens) => {
                const resolvedIsSelected = resolveLensIsSelectedForLens(lens.lens_id);
                if (resolvedIsSelected == null) {
                    return lens;
                }

                return { ...lens, is_selected: resolvedIsSelected };
            }),
        ),
    });
